import gql from 'graphql-tag';

import {
  instantPromotionFields,
  potentialPromotionsFragment
} from './potential-promotions-fragment';

import { PAGE_SIZE } from '../constants/products';

const plpQueriesAlcFields = gql`
  fragment plpQueriesAlcFields on ProductData {
    addToCartDisabled
    articleStatus
    articleStatusLabel
    productLaunchDate
  }
`;

const plpQueriesStoppingFields = gql`
        fragment plpQueriesStoppingFields on StoppingData {
          rollingResistanceRating
          wetRating
          wetDistance
          wetWornDistance
          dryDistance
          dryRating
          winterRating
        }
      `;

const windshieldWiperFields = gql`
  fragment windshieldWiperFields on ProductData {
    accessoryType
    bladeLength
    brand
    brandCategory
    fitmentPosition {
      code
      description
    }
  }
`;

export const facetsFragment = gql`
  fragment facetsFragment on FacetData_SearchStateData {
    category
    code
    isSlider
    description
    multiSelect
    name
    needCollapse
    needExpand
    needShowMore
    priority
    topValues {
      code
      count
      name
      query {
        listUrl
        query {
          value
        }
        url
      }
      scaleMaxVal
      scaleMinVal
      selected
      sliderMaxVal
      sliderMinVal
    }
    values {
      code
      count
      name
      plpToolTip
      query {
        listUrl
        query {
          value
        }
        url
      }
      scaleMaxVal
      scaleMinVal
      selected
      sliderMaxVal
      sliderMinVal
    }
    type
  }
`;

export const productFragment = gql` 
  fragment productFragment on ProductData {
    ...plpQueriesAlcFields
    ...windshieldWiperFields
    aggregates {
      allVehiclesReviewCount
      codes
      propertyRanges {
        name
        rangeValue
      }
      itemType
      specialCanonical
    }
    averageRating
    baseprice {
      value
      formattedValue
    }
    brandLogo {
      url
    }
    code
    color
    description
    freeShipping
    gbb
    icons {
      altText
    }
    images {
      url
      altText
      format
    }
    isCoreProduct
    isExclusive
    isOtherVehiclesOE
    isViewableOnVehicle
    origEquipmentData {
      description
      disPlayName
      name
    }
    line
    manufacturerAID
    manufacturerMileageWarranty
    mapDisplayRule {
      mapPriceDisplay
      message
      messageDisplay
    }
    mapPrice {
      formattedValue
      value
    }
    mapRuleSatisfied
    name
    potentialPromotions {
      ...potentialPromotionsFragment
    }
    price {
      value
      formattedValue
    }
    priceRange {
      minPrice {
        formattedValue
        value
      }
      maxPrice {
        formattedValue
        value
      }
    }
    productType
    rearProduct {
      ...plpQueriesAlcFields
      code
      description
      origEquipmentData {
        description
        disPlayName
        name
      }
      gbb
      brand
      brandLogo {
        url
      }
      images {
        url
        altText
        format
      }
      name
      productType
      potentialPromotions {
        ...potentialPromotionsFragment
      }
      price {
        value
        formattedValue
      }
      mapRuleSatisfied
      mapDisplayRule {
        mapPriceDisplay
        message
        messageDisplay
      }
      mapPrice {
        formattedValue
        value
      }
      size
      stock {
        purchaseDecisionValue
        relativeStockCount
        showCheckNearbyStoresLink
        stockCount
        availabilityMessage
        availabilityMessageRank
        stockLevelStatus {
          code
        }
      }
      markdownPriceData {
        formattedPercentageSaved
        originalRetailValue
      }
      manufacturerAID
      unitName
      wheelRimDiameter
      wheelWidth
    }
    reviewSummaryData {
      totalCount
      totalRating
    }
    size
    stock {
      cityOfStore
      nearbyStoresStockCount
      purchaseDecisionValue
      relativeStockCount
      showCheckNearbyStoresLink
      stockCount
      availabilityMessage
      availabilityMessageRank
      relativeInventory {
        isAvailable
        relativeInventoryStatus
        stockMessagingCode
      }
      vendorsStockCount
      stockLevelStatus {
        code
      }
    }
    unitName
    unitOfMeasureDescription
    url
    wheelFinish: finish
    wheelColor
    wheelRimDiameter
    wheelWidth
    swatches {
      code
      altText
      color
      hexCode: hexcode
      linkUrl
      isViewableOnVehicle
      wheelPartId
      productCode
      vendorFinish
    }
    vendorFinish
    canonicalFallBackUrl
    recommendationData {
      rideRating {
        handling
        quiet
        comfort
        wetHandling
      }
      tireLifeYears
      tireLifeMonths
      stopping {
        ...plpQueriesStoppingFields
      }
      rank
      mileage {
        median
        max
        warranty
        min
      }
      milesPerDollar
    }
    markdownPriceData {
      formattedPercentageSaved
      originalRetailValue
    }
  }
  ${instantPromotionFields}
  ${potentialPromotionsFragment}
  ${plpQueriesAlcFields}
  ${plpQueriesStoppingFields}
  ${windshieldWiperFields}
`;

export const productSearchResult = gql`
  fragment productSearchResult on ProductSearchResult {
    facets {
      ...facetsFragment
    }
    pagination {
      currentPage
      numberOfPages
      pageSize
      totalNumberOfResults
    }
    results {
      ...productFragment
    }
    sorts {
      code
      name
      selected
    }
    recommendationAvailable
  }
  ${facetsFragment}
  ${productFragment}
`;

const FITMENT_TIRE_PRODUCT_SEARCH = gql`
  query FitmentTireProductSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: StandardTireSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      standardTireSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getFitmentTireProductSearch = ({
  pageNum,
  storeCode,
  vehicleInput,
  searchOption,
  frontTireSize,
  treadwell,
  searchQuery
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.standardTireSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.standardTireSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.standardTireSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.standardTireSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.standardTireSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.standardTireSearch?.pagination?.totalNumberOfResults
  }),
  query: FITMENT_TIRE_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      searchOption,
      size: frontTireSize,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});

const FITMENT_STAGGERED_TIRE_PRODUCT_SEARCH = gql`
  query FitmentStaggeredTireProductSearch(
    $vehicleInfo: VehicleInput!
    $searchInput: StaggeredTireSearchInput!
    $storeCode: String!
  ) {
    fitmentSearch(vehicleInfo: $vehicleInfo) {
      staggeredTireSearch(searchInput: $searchInput, storeCode: $storeCode) {
        ...productSearchResult
      }
    }
  }
  ${productSearchResult}
`;

export const getFitmentStaggeredTireProductSearch = ({
  pageNum,
  storeCode,
  vehicleInput,
  searchOption,
  treadwell,
  searchQuery,
  staggeredSearchType,
  rearTireSize,
  frontTireSize
}) => ({
  mapResponse: data => ({
    filters: data?.fitmentSearch?.staggeredTireSearch?.facets,
    numberOfPages:
      data?.fitmentSearch?.staggeredTireSearch?.pagination?.numberOfPages,
    products: data?.fitmentSearch?.staggeredTireSearch?.results,
    recommendationAvailable:
      data?.fitmentSearch?.staggeredTireSearch?.recommendationAvailable,
    sorts: data?.fitmentSearch?.staggeredTireSearch?.sorts,
    totalNumberOfResults:
      data?.fitmentSearch?.staggeredTireSearch?.pagination?.totalNumberOfResults
  }),
  query: FITMENT_STAGGERED_TIRE_PRODUCT_SEARCH,
  variables: {
    searchInput: {
      front: frontTireSize,
      pageNumber: pageNum,
      pageSize: PAGE_SIZE,
      rawQuery: searchQuery,
      rear: rearTireSize,
      searchOption,
      staggeredType: staggeredSearchType,
      treadwell
    },
    storeCode,
    vehicleInfo: vehicleInput
  }
});
